import { addDays, format } from "date-fns";
import { sum } from "lodash";

import { store } from "app/store";
import { setDataParams } from "components/word-processor-field/word-processor.slice";
import { LEFT_EAR_TYPE, RIGHT_EAR_TYPE, RECEIVER_SUBTYPE, DOME_SUBTYPE } from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  FEE_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import type {
  DomeInventoryProductSpecification,
  ReceiverInventoryProductSpecification
} from "services/types/inventory-products.type";
import TITLES from "constants/titles";
import type { LineItem } from "services/types/sales.type";
import { formatMoneyValue, getFullName, roundTo } from "utils/common";

import { PATIENT_SELECTED_INVOICE_OPTIONS } from "../merge-tags-options/patient-selected-invoice.utils";
import {
  buildMergeTagOptionsString,
  getDateOrNull,
  getSaleMergeTagOrPlaceholder
} from "../merge-tags.utils";
import {
  getSaleDeviceLineItems,
  getSaleLineItemsServicesTableHtml,
  getSaleLineItemsTableHtml,
  getSaleLineItemsTableHtmlWithPredefinedParameters,
  getSaleLineItemsTotalsTableHtml
} from "./common/sale-items-table.utils";
import { MergeTagsReplaceHandler } from "./merge-tags-values";
import {
  getFirstHearingAidFromSale,
  getHearingAidDeliveryDate,
  getHearingAidSerialNumber,
  getHearingAidWarrantyEnd,
  getHearingAidWarrantyStart,
  getPatientProductEar
} from "./patient-selected-invoice-hearing-aids.utils";

const findAccessoryItem = (
  items: LineItem[] | undefined,
  subtype: number,
  ear: "L" | "R"
) =>
  items?.find((i) => {
    if (
      i.product?.master_product?.type !== ACCESSORY_PRODUCT_TYPE ||
      i.product?.master_product?.subtype !== subtype
    ) {
      return false;
    }
    // Check ear in multiple locations, matching the hearing-aid line-item pattern
    const patientProducts = (i as any).patient_products;
    const orderItem = (i as any).order_item;
    const itemEar =
      i.inventory_product?.ear ??
      (Array.isArray(patientProducts)
        ? getPatientProductEar(patientProducts[0])
        : undefined) ??
      orderItem?.patient_product?.ear;
    return itemEar === ear;
  });

export const PATIENT_SELECTED_INVOICE_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_SELECTED_INVOICE_OPTIONS.SELECT_AN_INVOICE]: ({ id }) => {
    store.dispatch(setDataParams({ id, openSelectSaleDialog: true }));
    return null;
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.DEFINE_PARAMETERS]: ({
    id,
    data,
    options
  }) => {
    store.dispatch(
      setDataParams({ id, openSelectSaleConfigurationDialog: true })
    );
    return null;
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS]: ({
    id,
    data,
    options
  }) => {
    if (!options || !Object.values(options).length) {
      store.dispatch(
        setDataParams({ id, openSelectSaleConfigurationDialog: true })
      );
      return null;
    }

    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTableHtmlWithPredefinedParameters(
        data.contextSale,
        options
      ),
      PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS,
      buildMergeTagOptionsString(options)
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.SALE_ITEMS]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTableHtml(
        data.contextSale,
        getSaleDeviceLineItems,
        true,
        false,
        true
      ),
      PATIENT_SELECTED_INVOICE_OPTIONS.SALE_ITEMS
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTableHtml(data.contextSale, getSaleDeviceLineItems),
      PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES_NO_PRICE]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTableHtml(
        data.contextSale,
        getSaleDeviceLineItems,
        false,
        true
      ),
      PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES_NO_PRICE
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsServicesTableHtml(data.contextSale),
      PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES_NO_PRICE]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsServicesTableHtml(data.contextSale, true),
      PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES_NO_PRICE
    );
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.TOTALS]: ({ data }) => {
    return getSaleMergeTagOrPlaceholder(
      getSaleLineItemsTotalsTableHtml(data.contextSale),
      PATIENT_SELECTED_INVOICE_OPTIONS.TOTALS
    );
  },
  //@@
  [PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_HEARING_AIDS]: ({ data }) => {
    const items = data.contextSale?.items as LineItem[];
    if (!items || items.length === 0) return null;

    const totalHearingAids = roundTo(
      sum(
        items
          .filter(
            (item) =>
              item.product?.master_product?.type === HEARING_AID_PRODUCT_TYPE
          )
          .map(
            (item) => (Number(item.price) || 0) * (Number(item.quantity) || 1)
          ) || []
      ),
      2
    );

    return formatMoneyValue(totalHearingAids);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_SERVICES]: ({ data }) => {
    const items = data.contextSale?.items as LineItem[];
    if (!items || items?.length === 0) return null;

    const totalServices = roundTo(
      sum(
        items
          .filter(
            (item) =>
              item.product?.master_product?.type === SERVICE_PRODUCT_TYPE ||
              item.product?.master_product?.type === FEE_PRODUCT_TYPE
          )
          .map(
            (item) => (Number(item.price) || 0) * (Number(item.quantity) || 1)
          ) || []
      ),
      2
    );

    return formatMoneyValue(totalServices);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.TRIAL_PERIOD_DURATION_DAYS]: ({
    data,
    extraData
  }) => {
    if (!extraData) return null;

    const value = extraData.trialPeriodDurationDays;

    return value ? `${value} day${Number(value) > 1 ? "s" : ""}` : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RESTOCKING_RETURN_FEE]: ({ extraData }) => {
    if (!extraData) return null;

    const value = extraData.restockingReturnFee;

    return value ? formatMoneyValue(value) : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LOSS_DAMAGE_DEDUCTIBLE]: ({
    extraData
  }) => {
    if (!extraData) return null;

    const value = extraData.lossDamageDeductible;

    return value ? formatMoneyValue(value) : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.WARRANTY_DURATION]: ({ extraData }) => {
    if (!extraData) return null;

    const value = extraData.warrantyDuration;

    return value ? `${value} month${Number(value) > 1 ? "s" : ""}` : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.MEMO]: ({ data, extraData }) => {
    if (!extraData) return null;

    const value = extraData.memo;
    return value ?? "";
  },
  //
  [PATIENT_SELECTED_INVOICE_OPTIONS.PURCHASE_AGREEMENT_DATE]: ({
    extraData
  }) => {
    if (!extraData) return null;

    const value = getDateOrNull(extraData.purchaseAgreementDate);
    return value ? format(value, "MM/dd/yyyy") : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.TRIAL_PERIOD_END_DATE]: ({ extraData }) => {
    if (!extraData) return null;

    const value = Number(extraData.trialPeriodDurationDays);
    const purchaseAgreementDate = getDateOrNull(
      extraData.purchaseAgreementDate
    );
    if (isNaN(value) || !purchaseAgreementDate) return "";

    return format(addDays(purchaseAgreementDate, value), "MM/dd/yyyy");
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_FULL_NAME]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;

    return getFullName({
      firstName: provider?.first_name ?? "",
      lastName: provider?.last_name ?? ""
    });
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_FULL_NAME_WITH_TITLE]: ({
    data
  }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return getFullName({
      firstName: provider?.first_name ?? "",
      lastName: provider?.last_name ?? "",
      title: provider?.title
    });
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_FIRST_NAME]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.first_name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_LAST_NAME]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.last_name ?? "";
  },

  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_TITLE]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const title = data.contextSale.provider?.title ?? "";
    return title ? TITLES.getFromCode(title) : "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_SUFFIX]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.suffix ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_LICENSE_NUMBER]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.license_number ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_SIGNATURE]: ({ data }) => {
    if (!data.contextSale?.provider) return null;

    const provider = data.contextSale.provider;
    return provider?.signature
      ? `<img src="${provider?.signature}" alt="Provider mergetag signature" width="350" />`
      : null;
  },

  // Left Hearing Aid merge tags
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_MANUFACTURER]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_MODEL]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_SERIAL_NUMBER]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return getHearingAidSerialNumber(item, LEFT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_WARRANTY_START]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return getHearingAidWarrantyStart(item, LEFT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_WARRANTY_END]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return getHearingAidWarrantyEnd(item, LEFT_EAR_TYPE);
  },

  // Right Hearing Aid merge tags
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_MANUFACTURER]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_MODEL]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_SERIAL_NUMBER]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return getHearingAidSerialNumber(item, RIGHT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_WARRANTY_START]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return getHearingAidWarrantyStart(item, RIGHT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_WARRANTY_END]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return getHearingAidWarrantyEnd(item, RIGHT_EAR_TYPE);
  },

  // Hearing Aid Delivery Date merge tags
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_DELIVERY_DATE]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    return getHearingAidDeliveryDate(item, LEFT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_DELIVERY_DATE]: ({ data }) => {
    const item = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    return getHearingAidDeliveryDate(item, RIGHT_EAR_TYPE);
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.HA_DELIVERY_DATE]: ({ data }) => {
    // No invoice selected: show the "Click to Select Invoice" placeholder.
    if (!data.contextSale) {
      return getSaleMergeTagOrPlaceholder(null, PATIENT_SELECTED_INVOICE_OPTIONS.HA_DELIVERY_DATE);
    }

    const leftItem = getFirstHearingAidFromSale(data.contextSale, LEFT_EAR_TYPE);
    const rightItem = getFirstHearingAidFromSale(data.contextSale, RIGHT_EAR_TYPE);
    const left = getHearingAidDeliveryDate(leftItem, LEFT_EAR_TYPE) || null;
    const right = getHearingAidDeliveryDate(rightItem, RIGHT_EAR_TYPE) || null;

    // Invoice selected but no delivery dates: return empty (not the placeholder).
    if (left && right && left === right) return left;
    if (left && right) return `L: ${left} R: ${right}`;
    if (left) return `L: ${left}`;
    if (right) return `R: ${right}`;
    return "";
  },

  // Receiver merge tags (CS-5372)
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_MANUFACTURER]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, RECEIVER_SUBTYPE, "L");
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_MODEL]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, RECEIVER_SUBTYPE, "L");
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_GAIN]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, RECEIVER_SUBTYPE, "L");
    if (!item) return "";
    const spec =
      item.inventory_product?.specification as ReceiverInventoryProductSpecification | undefined;
    return spec?.gain ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_LENGTH]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, RECEIVER_SUBTYPE, "L");
    if (!item) return "";
    const spec =
      item.inventory_product?.specification as ReceiverInventoryProductSpecification | undefined;
    return spec?.length != null ? String(spec.length) : "";
  },

  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_MANUFACTURER]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, RECEIVER_SUBTYPE, "R");
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_MODEL]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, RECEIVER_SUBTYPE, "R");
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_GAIN]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, RECEIVER_SUBTYPE, "R");
    if (!item) return "";
    const spec =
      item.inventory_product?.specification as ReceiverInventoryProductSpecification | undefined;
    return spec?.gain ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_LENGTH]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, RECEIVER_SUBTYPE, "R");
    if (!item) return "";
    const spec =
      item.inventory_product?.specification as ReceiverInventoryProductSpecification | undefined;
    return spec?.length != null ? String(spec.length) : "";
  },

  // Dome merge tags (CS-5372)
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_MANUFACTURER]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "L");
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_MODEL]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "L");
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_SIZE]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "L");
    if (!item) return "";
    const spec =
      item.inventory_product?.specification as DomeInventoryProductSpecification | undefined;
    return spec?.size ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_TYPE]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "L");
    if (!item) return "";
    const spec =
      item.inventory_product?.specification as DomeInventoryProductSpecification | undefined;
    return spec?.type ?? "";
  },

  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_MANUFACTURER]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "R");
    return item?.product?.master_product?.manufacturer?.name ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_MODEL]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "R");
    return item?.product?.master_product?.model ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_SIZE]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "R");
    if (!item) return "";
    const spec =
      item.inventory_product?.specification as DomeInventoryProductSpecification | undefined;
    return spec?.size ?? "";
  },
  [PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_TYPE]: ({ data }) => {
    const item = findAccessoryItem(data.contextSale?.items, DOME_SUBTYPE, "R");
    if (!item) return "";
    const spec =
      item.inventory_product?.specification as DomeInventoryProductSpecification | undefined;
    return spec?.type ?? "";
  }
};
